import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import useNavigationLinks, { NavTreeNode } from 'hooks/useNavigationLinks';
import useLogout from 'hooks/useLogout';
import { Icon } from 'components/ui';
import {
  collectActiveAncestorIds,
  collectAllBranchIds,
  filterNavTreeByQuery,
} from 'components/Navigation/SideNav/navTree';
import {
  isDevUserSwitchEnabled,
  setDevSwitcherVisible,
} from 'utils/devUserSwitch';
import Wordmark from './Wordmark';
import SidebarUserCard from './SidebarUserCard';
import SidebarSearch from './SidebarSearch';
import SidebarNavItem from './SidebarNavItem';
import styles from './Sidebar.module.scss';

export interface ISidebarProps {
  className?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
  onNavigate?: () => void;
  footer?: React.ReactNode;
}

function mapNodeIcon(node: NavTreeNode): React.ReactNode {
  if (node.id === 'profile' || node.id === 'home') {
    return <Icon name="user" size={20} />;
  }
  if (node.id === 'txt-office') {
    return <Icon name="building" size={20} />;
  }
  return node.icon;
}

export default function Sidebar({
  className,
  showCloseButton = false,
  onClose,
  onNavigate,
  footer,
}: ISidebarProps) {
  const { links } = useNavigationLinks();
  const location = useLocation();
  const { logout } = useLogout();

  const [navSearchInput, setNavSearchInput] = useState('');
  const [debouncedNavSearch, setDebouncedNavSearch] = useState('');

  const setDebouncedNavSearchDebounced = useMemo(
    () => debounce((value: string) => setDebouncedNavSearch(value), 300),
    [],
  );

  useEffect(
    () => () => setDebouncedNavSearchDebounced.cancel(),
    [setDebouncedNavSearchDebounced],
  );

  const filteredLinks = useMemo(() => {
    const q = debouncedNavSearch.trim().toLowerCase();
    if (!q) return links;
    const tokens = q.split(/\s+/).filter(Boolean);
    return filterNavTreeByQuery(links, tokens);
  }, [links, debouncedNavSearch]);

  const displayLinks = debouncedNavSearch.trim() ? filteredLinks : links;

  const activeAncestorIds = useMemo(
    () => collectActiveAncestorIds(links, location.pathname),
    [links, location.pathname],
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (debouncedNavSearch.trim()) {
      setExpandedIds(new Set(collectAllBranchIds(filteredLinks)));
      return;
    }
    setExpandedIds(new Set(activeAncestorIds));
  }, [debouncedNavSearch, filteredLinks, activeAncestorIds]);

  const handleSearchChange = (value: string) => {
    setNavSearchInput(value);
    setDebouncedNavSearchDebounced(value);
  };

  const toggleNodeExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleNavigate = () => {
    onNavigate?.();
  };

  const renderLeaf = (node: NavTreeNode) => {
    if (!node.to) return null;
    return (
      <li key={node.id}>
        <SidebarNavItem
          icon={mapNodeIcon(node)}
          label={node.title}
          to={node.to}
          subtitle={node.subtitle}
          statusTone={node.statusTone}
          onClick={handleNavigate}
        />
      </li>
    );
  };

  const renderNode = (node: NavTreeNode): React.ReactNode => {
    if (!node.children || node.children.length === 0) {
      return renderLeaf(node);
    }

    const isExpanded = expandedIds.has(node.id);
    return (
      <li key={node.id}>
        <SidebarNavItem
          icon={mapNodeIcon(node)}
          label={node.title}
          active={activeAncestorIds.has(node.id)}
          trailing={isExpanded ? 'collapse' : 'expand'}
          onClick={() => toggleNodeExpanded(node.id)}
        />
        {isExpanded ? (
          <ul className={styles.childList}>
            {node.children.map(child => renderNode(child))}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <aside
      className={clsx(styles.sidebar, className)}
      aria-label="My TXT navigation"
    >
      <div className={styles.brandRow}>
        <Wordmark onNavigate={handleNavigate} />
        {showCloseButton ? (
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close menu"
            onClick={onClose}
          >
            <Icon name="x" size={20} className={styles.closeIcon} />
          </button>
        ) : null}
      </div>

      <SidebarUserCard />
      <SidebarSearch
        value={navSearchInput}
        onChange={handleSearchChange}
      />

      <nav className={styles.nav} aria-label="Primary">
        <ul className={styles.list}>
          {displayLinks.length === 0 && debouncedNavSearch.trim() ? (
            <li className={styles.empty}>No matching links</li>
          ) : (
            displayLinks.map((node, idx) => (
              <React.Fragment key={node.id}>
                {node.sectionTitle && !debouncedNavSearch.trim() ? (
                  <li
                    role="presentation"
                    className={clsx(
                      styles.sectionLabel,
                      idx === 0 && styles.sectionLabelFirst,
                    )}
                  >
                    {node.sectionTitle}
                  </li>
                ) : null}
                {renderNode(node)}
              </React.Fragment>
            ))
          )}
          <li>
            <SidebarNavItem
              icon={<Icon name="log-out" size={20} />}
              label="Sign Out"
              onClick={() => {
                handleNavigate();
                logout();
              }}
            />
          </li>
          {isDevUserSwitchEnabled() ? (
            <li>
              <SidebarNavItem
                icon={<Icon name="menu" size={20} />}
                label="Show dev switcher"
                onClick={() => {
                  setDevSwitcherVisible(true);
                  handleNavigate();
                }}
              />
            </li>
          ) : null}
        </ul>
      </nav>

      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </aside>
  );
}
